import type { DocOption } from "building-types/options/optionSet";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import OptionSelectCircle from "lib/components/builders/communities/plans/OptionSelectCircle";
import { useMemo } from "react";
import useAppContext from "ui/hooks/useAppContext";
import IconEllipsis from "ui/icons/IconEllipsis";
import IconTooltip from "ui/icons/IconTooltip";
import Tooltip from "ui/popovers/Tooltip";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import { PlanLibraryBadge } from "./PlanLibraryBadge";
import { extraOptionMsg } from "./PlanLibraryMessages";
import styles from "./PlanLibraryOptionSets.module.scss";
import { PlanLibraryOptionValue } from "./PlanLibraryOptionValue";
import { planLibraryActions } from "./planLibraryReducer";
import { PlanLibraryRuleBadge } from "./PlanLibraryRuleBadge";
import { usePlanLibraryDispatch, usePlanLibrarySelector } from "./planLibraryStore";
import type { PlanLibraryOptionSet } from "./planLibraryTypes";
import { planLibraryGetInheritedAndCurrentPrice, planLibraryIsOptionSetOverridden } from "./planLibraryUtils";

function toggleDisabled(optionSet: PlanLibraryOptionSet, isDisabled: boolean) {
  return {
    ...optionSet,
    disabled: !isDisabled,
    options: optionSet.options.map((o) => {
      if (o.id === "true" && o.disabled) {
        return { ...o, disabled: false };
      }
      return o;
    }),
  };
}

function ExtraOptionsTooltip() {
  return (
    <Tooltip.Root delayDuration={0}>
      <Tooltip.Trigger>
        <div>
          <IconTooltip color="stoneDark" size={16}></IconTooltip>
        </div>
      </Tooltip.Trigger>
      <Tooltip.Content>
        <div className={styles.tooltip}>{extraOptionMsg}</div>
      </Tooltip.Content>
    </Tooltip.Root>
  );
}

export default function PlanLibraryBooleanOptionSets({ salesCategoryName }: { salesCategoryName: string }) {
  const { setUserNotification } = useAppContext();
  const dispatch = usePlanLibraryDispatch();

  const {
    booleanOptionSetsPerSalesCategory,
    optionSetEntityRelations,
    inheritedOptionSets,
    isReadOnly,
    overrides,
    searchFor,
    builder,
  } = usePlanLibrarySelector((state) => ({
    booleanOptionSetsPerSalesCategory: state.booleanOptionSetsPerSalesCategory,
    optionSetEntityRelations: state.optionSetEntityRelations,
    inheritedOptionSets: state.inheritedOptionSets,
    isReadOnly: state.isReadOnly,
    overrides: state.overrides,
    searchFor: state.searchFor,
    builder: state.builder,
  }));

  const optionSets: PlanLibraryOptionSet[] = booleanOptionSetsPerSalesCategory[salesCategoryName].filter(
    ({ isFilteredBySearch }) => !isFilteredBySearch,
  );

  function isOptionValueOverridden(optionSetId: string, key: keyof DocOption) {
    return overrides[optionSetId]?.options?.true?.[key] !== undefined;
  }

  function getInheritedOptionValue(optionSetId: string, key: keyof DocOption) {
    return inheritedOptionSets[optionSetId]?.options?.find((x) => x.id === "true")?.[key];
  }

  const columns: Column<PlanLibraryOptionSet>[] = useMemo(() => {
    return [
      {
        header: "Upgrade Name",
        bold: true,
        width: 0.4,
        cell: ({ row }) => {
          const optionSet = row.original;
          const noFloorPlanEffect = optionSetEntityRelations.optionSetIdToAffectedEntities[optionSet.id] === undefined;
          const optionSetOverride = overrides[optionSet.id];
          const inheritedOptionSet = inheritedOptionSets[optionSet.id];

          return (
            <div className={styles.tableOptionSetNameWrapper}>
              {noFloorPlanEffect && <ExtraOptionsTooltip />}

              <PlanLibraryOptionValue
                field="Name"
                isOverridden={optionSetOverride?.name !== undefined}
                inheritedValue={inheritedOptionSet?.name}
              >
                <div style={{ width: "386px" }} className={styles.optionNameWrapper}>
                  {optionSet.name}
                </div>
              </PlanLibraryOptionValue>

              <div className={styles.tableOptionSetBadgeWrapper}>
                <PlanLibraryRuleBadge optionSet={optionSet} />
                <PlanLibraryBadge
                  isOverridden={planLibraryIsOptionSetOverridden(optionSetOverride, optionSet)}
                  onClickBadge={() => {
                    dispatch(planLibraryActions.enableOverrideModal({ optionSet }));
                    dispatch(
                      planLibraryActions.setOverridesToDiscard({
                        overridesToDiscard: optionSetOverride,
                        onHandleDiscarding: () => {
                          dispatch(planLibraryActions.discardOptionSetOverride({ optionSet }));
                          setUserNotification({
                            message: `Overrides discarded for ${optionSet.name}`,
                            messageType: "success",
                          });
                        },
                      }),
                    );
                  }}
                />
              </div>
            </div>
          );
        },
      },

      {
        header: "Price",
        width: 0.09,
        cell: ({ row }) => {
          const optionSet = row.original;
          const option = optionSet.options.find((option) => option.id === "true");

          assertIsNotUndefined(option);

          const { inheritPrice, currentPrice } = planLibraryGetInheritedAndCurrentPrice(
            builder,
            inheritedOptionSets[optionSet.id],
            overrides[optionSet.id],
            option,
          );

          return (
            <PlanLibraryOptionValue
              field="Price"
              isOverridden={
                isOptionValueOverridden(optionSet.id, "priceTbd") || isOptionValueOverridden(optionSet.id, "price")
              }
              inheritedValue={inheritPrice}
            >
              {currentPrice}
            </PlanLibraryOptionValue>
          );
        },
      },

      {
        header: "Builder Option ID",
        cell: ({ row }) => {
          const optionSet = row.original;
          const option = optionSet.options.find((option) => option.id === "true");
          assertIsNotUndefined(option);

          return (
            <PlanLibraryOptionValue
              field="Builder Option ID"
              isOverridden={isOptionValueOverridden(optionSet.id, "builderOptionCode")}
              inheritedValue={getInheritedOptionValue(optionSet.id, "builderOptionCode")}
            >
              <div style={{ width: "200px" }} className={styles.optionNameWrapper}>
                {option.builderOptionCode}
              </div>
            </PlanLibraryOptionValue>
          );
        },
      },

      {
        header: "Enabled",
        width: 0.1,
        cell: ({ row }) => {
          const optionSet: PlanLibraryOptionSet = row.original;
          const isDisabled =
            optionSet.disabled === true || optionSet.options.find((option) => option.id === "true")?.disabled === true;
          return (
            <div
              className={styles.enabledCheckboxWrapper}
              onClick={(e) => {
                if (!isReadOnly) {
                  const oldOptionSet = optionSet;
                  const newOptionSet = toggleDisabled(optionSet, isDisabled);
                  dispatch(planLibraryActions.overrideOptionSet({ oldOptionSet, newOptionSet }));
                }
                e.stopPropagation();
              }}
            >
              <OptionSelectCircle
                selected={!isDisabled}
                highlighted={
                  overrides[optionSet.id]?.disabled !== undefined ||
                  overrides[optionSet.id]?.options?.true?.disabled !== undefined
                }
                disabled={isReadOnly}
              />
            </div>
          );
        },
      },
    ];
  }, [optionSetEntityRelations, inheritedOptionSets, overrides, isReadOnly, builder, dispatch, setUserNotification]);

  return (
    <>
      <h4 style={optionSets.length === 0 ? { display: "none" } : {}} className={styles.upgradesHeader}>
        Upgrades
      </h4>
      <div className={styles.optionTableWrapper}>
        <ConfigTable
          rowGaps
          data={optionSets}
          columns={columns}
          draggable={!isReadOnly && optionSets.length > 1 && !searchFor}
          onRowDrop={async (optionSets) => {
            const pairs = optionSets.map((oldOptionSet, newSortIndex) => ({
              oldOptionSet,
              newSortIndex,
            }));
            dispatch(
              planLibraryActions.reorderOptionSetsInSalesCategory({
                salesCategoryName,
                sortTuples: pairs,
              }),
            );
          }}
          onRowClick={(row) => {
            if (!isReadOnly) {
              const optionSet = row.original;
              dispatch(planLibraryActions.enableOverrideModal({ optionSet }));
            }
          }}
          overflowMenu={(row) => {
            if (!isReadOnly) {
              const optionSet = row.original;
              return (
                <IconEllipsis
                  onClick={() => dispatch(planLibraryActions.enableOverrideModal({ optionSet }))}
                  size={16}
                />
              );
            }
            return null;
          }}
        />
      </div>
    </>
  );
}
